/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_LITHOLOGY, TXT_LITHOLOGIES } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addLithologyInput,
  deleteLithologyInput,
  getLithologiesInput,
  getLithologyOutput,
  GET_LITHOLOGY,
  updateLithologyInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageLithologysTable } from './ManageLithologysTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { lithology } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_LITHOLOGY,
  DELETE_LITHOLOGY,
  UPDATE_LITHOLOGY
} from '../../../api';
import { ManageLithologyEditor } from './ManagerLithologyEditor';

export const ManageLithologys = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<lithology[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedLithology, setSelectedLithology] = useState<lithology | undefined>(undefined);
  const [getLithologys, { loading, data, error, refetch }] = useLazyQuery<getLithologyOutput>(GET_LITHOLOGY);
  const [getAllAlterations] = useLazyQuery<getLithologyOutput>(GET_LITHOLOGY, {
    onCompleted: (data) => setAlterationData(data.lithologies.items)
  });

  useEffect(() => {
    getLithologys();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchLithologys = () => {
    const variables: getLithologiesInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getLithologys({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchLithologys();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditLithologyEditor = (lithology: lithology) => {
    setSelectedLithology(lithology);
    setOpenEditor(true);
  };

  const openDeleteLithologyConfirmation = (lithology: lithology) => {
    setSelectedLithology(lithology);
    setOpenDeleteConfirmation(true);
  };

  const openAddLithologyEditor = () => {
    setSelectedLithology(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingLithology = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingLithologyError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addLithology, addLithologyOperation] = useMutation<lithology>(ADD_LITHOLOGY, {
    onError: (e) => handleAddingOrUpdatingLithologyError(e),
    onCompleted: (result) => afterAddingOrUpdatingLithology()
  });

  const [updateLithology, updateLithologyOperation] = useMutation<lithology>(UPDATE_LITHOLOGY, {
    onError: (e) => handleAddingOrUpdatingLithologyError(e),
    onCompleted: (result) => afterAddingOrUpdatingLithology()
  });

  const onEditorSave = (payload: addLithologyInput | updateLithologyInput) => {
    if (!selectedLithology) {
      addLithology({
        variables: {
          input: payload
        }
      });
    } else {
      updateLithology({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteLithology, deleteLithologyOperation] = useMutation<boolean>(DELETE_LITHOLOGY, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.lithologies.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteLithologyInput = {
      id: selectedLithology!.id!
    };
    deleteLithology({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_LITHOLOGIES))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageLithologyEditor
              lithology={selectedLithology}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedLithology ? addLithologyOperation.loading :
                  updateLithologyOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteLithologyOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedLithology?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-lithology-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddLithologyEditor}>
                        {t(TXT_ADD_LITHOLOGY).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_LITHOLOGIES} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageLithologysTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditLithologyEditor}
                    onDelete={openDeleteLithologyConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.lithologies.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
